import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import logo from "@/assets/logo.webp";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";

type FooterLink = { label: string; to?: string; href?: string };

const COLUMNS: { heading: string; links: FooterLink[] }[] = [
  {
    heading: "Product",
    links: [
      { label: "Features", href: "#features" },
      { label: "Pricing", href: "#pricing" },
      { label: "FAQ", href: "#faq" },
      { label: "Free lineup builder", to: "/lineup-builder" },
      { label: "Install app", to: "/help/install" },
      { label: "Pickleball", to: "/pickleball" },
    ],
  },
  {
    heading: "Compare",
    links: [
      { label: "All comparisons", to: "/alternatives" },
      { label: "vs Spond", to: "/alternatives/spond" },
      { label: "vs TeamSnap", to: "/alternatives/teamsnap" },
      { label: "vs PlayerLineup", to: "/alternatives/playerlineup" },
    ],
  },
  {
    heading: "Guides",
    links: [
      { label: "5-a-side formations", to: "/guides/5-a-side-formations" },
      { label: "7-a-side formations", to: "/guides/7-a-side-formations" },
      { label: "9-a-side formations", to: "/guides/9-a-side-formations" },
      { label: "Best formation for U11", to: "/guides/best-formation-for-u11" },
      { label: "9-a-side positions", to: "/guides/9-a-side-positions-explained" },
    ],
  },
];

const linkClass = "text-sm text-muted-foreground transition-colors hover:text-foreground";

const FooterSection = () => {
  return (
    <footer className="border-t border-border/60 bg-muted/20 pt-16 md:pt-20">
      <div className="container mx-auto px-4">
        <div className="grid gap-10 md:grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,1fr))] md:gap-8">
          <div className="max-w-xs">
            <div className="flex items-center gap-2">
              <img
                src={logo}
                alt="LineupSync logo"
                width={28}
                height={28}
                className="h-7 w-7 rounded-lg object-contain"
              />
              <span className="font-display font-bold text-foreground">LineupSync</span>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              Free team management for grassroots clubs: schedule, one-click RSVPs and lineups in one place.
            </p>
            <a
              href="https://www.instagram.com/lineupsync/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LineupSync on Instagram"
              className="mt-4 inline-flex text-muted-foreground transition-colors hover:text-foreground"
            >
              <Instagram className="h-5 w-5" />
            </a>
          </div>

          {COLUMNS.map((col) => (
            <nav key={col.heading} aria-label={col.heading}>
              <h3 className="font-display text-xs font-bold uppercase tracking-wider text-foreground">
                {col.heading}
              </h3>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {l.to ? (
                      <Link to={l.to} className={linkClass}>
                        {l.label}
                      </Link>
                    ) : (
                      <a href={l.href} className={linkClass}>
                        {l.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-border/60 py-6 text-xs text-muted-foreground md:flex-row">
          <p>© {new Date().getFullYear()} LineupSync. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/auth" className="transition-colors hover:text-foreground">Sign in</Link>
            <Link to="/privacy" className="transition-colors hover:text-foreground">Privacy</Link>
            <Link to="/terms" className="transition-colors hover:text-foreground">Terms</Link>
            <LanguageSwitcher current="en" />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;
